import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle2Icon,
  XCircleIcon,
  Loader2Icon,
} from 'lucide-react';
import { useAuthStore } from '@/app/store/authStore';
import { Wordmark } from '@/shared/components/Wordmark';
import { ThemeToggle } from '@/shared/components/ThemeToggle';

type Status = 'confirming' | 'success' | 'invalid';

export const ConfirmEmailChange: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirmEmailChange = useAuthStore((s) => s.confirmEmailChange);

  const userId = searchParams.get('userId') ?? '';
  const token = searchParams.get('token') ?? '';
  const hasLink = Boolean(userId && token);

  const [status, setStatus] = useState<Status>(hasLink ? 'confirming' : 'invalid');
  const [message, setMessage] = useState<string>(
    hasLink ? '' : 'This confirmation link is invalid or incomplete.'
  );

  // Guard against the effect running twice (React StrictMode) — the token is single-use, so a
  // second attempt would always report "invalid or expired".
  const attempted = useRef(false);

  useEffect(() => {
    if (!hasLink || attempted.current) return;
    attempted.current = true;

    (async () => {
      const result = await confirmEmailChange(userId, token);
      if (result.success) {
        setStatus('success');
        setMessage(result.message || 'Your email address has been updated.');
      } else {
        setStatus('invalid');
        setMessage(result.message || 'This confirmation link is invalid or has expired.');
      }
    })();
  }, [hasLink, userId, token, confirmEmailChange]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-12 sm:px-6 lg:px-8">
      {/* Brand-tinted ambient glow (subtle in both themes) */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />

      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center">
          <Wordmark className="scale-110" />
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-foreground">
            Confirm email change
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Feed aggregation, classification &amp; task tracking
          </p>
        </div>

        <div className="card mt-8 p-8 shadow-modal">
          {status === 'confirming' && (
            <div className="flex flex-col items-center text-center">
              <Loader2Icon className="h-12 w-12 animate-spin text-primary" />
              <h2 className="mt-4 text-lg font-semibold text-foreground">
                Confirming your new email…
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                This will only take a moment.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success-subtle">
                <CheckCircle2Icon className="h-8 w-8 text-success" />
              </span>
              <h2 className="mt-4 text-lg font-semibold text-foreground">
                Email address updated
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                For your security, you&apos;ve been signed out of your other sessions.
              </p>
              <button onClick={() => navigate('/')} className="btn-primary mt-6 w-full">
                Continue to the app
              </button>
            </div>
          )}

          {status === 'invalid' && (
            <div className="flex flex-col items-center text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-danger-subtle">
                <XCircleIcon className="h-8 w-8 text-danger" />
              </span>
              <h2 className="mt-4 text-lg font-semibold text-foreground">
                Confirmation failed
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Your email address was not changed. You can request the change again from your
                profile settings.
              </p>
              <Link
                to="/login"
                className="mt-6 text-sm font-medium text-primary hover:text-primary/80"
              >
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
